import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, throwError } from 'rxjs';
import { AppSettings } from '../app-settings';

@Injectable({
  providedIn: 'root'
})
export class TicketsService {

  private saveBooking = new BehaviorSubject<any>(null);
  public updateBooking = this.saveBooking.asObservable();

  private ticketsVenueObj = new BehaviorSubject<any>(null);
  public tickets = this.ticketsVenueObj.asObservable();

  private ticketsNumObj = new BehaviorSubject<any>(null);
  public ticketsNum = this.ticketsNumObj.asObservable();

  private configTicketsObj = new BehaviorSubject<any>(null);
  public configTickets = this.configTicketsObj.asObservable();

  constructor(private http: HttpClient) { }

  setBookingsStorage(bookings: any) {
    this.saveBooking.next(bookings);
  }

  getTicketsSaved() {
    return this.ticketsVenueObj.getValue();
  }

  setTicketsVenue(tickets: any) {
    this.ticketsVenueObj.next(tickets);
  }

  getTicketsNum() {
    return this.ticketsNumObj.getValue();
  }

  setTicketsNum(tickets: any) {
    this.ticketsNumObj.next(tickets);
  }

  getTicketsConfig() {
    return this.configTicketsObj.getValue();
  }

  setTicketsConfig(tickets: any) {
    this.configTicketsObj.next(tickets);
  }

  // ── Butacas especiales ───────────────────────────────────
  // Una butaca es "especial" (silla, movilidad reducida...) porque alguna entrada
  // la nombra en sus `ticket_seats`: no hay marca en el mapa que las distinga. El
  // catálogo de butacas entre las que puede elegir una entrada nueva se saca, por
  // tanto, de las entradas que ya existen (las del espacio o las del evento).

  specialSeatsPool(tickets: any[]): any[] {
    const seats = new Map<number, any>();
    (tickets || []).forEach((ticket: any) => {
      (ticket.ticket_seats || []).forEach((ticketSeat: any) => {
        const seat = ticketSeat.seat;
        if (seat?.id && !seats.has(seat.id)) {
          seats.set(seat.id, seat);
        }
      });
    });
    return [...seats.values()].sort((a: any, b: any) =>
      (a.id_level - b.id_level) || String(a.row).localeCompare(String(b.row)) || (a.seat - b.seat)
    );
  }

  // Conjuntos de butacas que ya usa alguna entrada, para copiarlos de un clic al
  // crear otra (p. ej. la invitación de las mismas sillas). Las entradas que
  // comparten butacas se colapsan en un único conjunto, con el nombre más corto.
  specialSeatGroups(tickets: any[]): { name: string; seatIds: number[] }[] {
    const groups = new Map<string, { name: string; seatIds: number[] }>();
    (tickets || [])
      .filter((ticket: any) => ticket.ticket_seats?.length > 0)
      .forEach((ticket: any) => {
        const seatIds = ticket.ticket_seats
          .map((ticketSeat: any) => ticketSeat.seat?.id)
          .filter((id: any) => !!id)
          .sort((a: number, b: number) => a - b);
        if (seatIds.length === 0) return;

        const key = seatIds.join('-');
        const existing = groups.get(key);
        if (!existing) {
          groups.set(key, { name: ticket.name, seatIds });
        } else if (ticket.name?.length < existing.name.length) {
          existing.name = ticket.name;
        }
      });
    return [...groups.values()];
  }

  createTicket(idEvent: number, payload: any) {
    const url = `${AppSettings.API_URI_V2}/tickets/event/${idEvent}`;
    return this.http.post<any>(url, payload).pipe(
      map((data) => {
        return data
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  createTicketForSpace(idSpace: number, payload: any) {
    const url = `${AppSettings.API_URI_V2}/tickets/space/${idSpace}`;
    return this.http.post<any>(url, payload).pipe(
      map((data) => {
        return data
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  editTicket(idTicket: any, payload: any) {
    const url = `${AppSettings.API_URI_V2}/tickets/${idTicket}`;
    return this.http.put<any>(url, payload).pipe(
      map((data) => {
        return data
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  updateTicketLevelPrice(ticketId: number, levelId: number, price: number | null) {
    const url = `${AppSettings.API_URI_V2}/tickets/${ticketId}/levels/${levelId}`;
    return this.http.put<any>(url, { price }).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  deleteTicket(idTicket: any) {
    const url = `${AppSettings.API_URI_V2}/tickets/${idTicket}`;
    return this.http.delete<any>(url).pipe(
      map((data) => {
        return data
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  checkHiddenCode(code: any, session: number) {
    const url = `${AppSettings.API_URI_V2}/sessions/${session}/blocked-seats-code/${code}`;
    return this.http.get<any>(url).pipe(
      map((data) => {
        return data
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  getTicketsByEvent(idEvent: any) {
    const url = `${AppSettings.API_URI_V2}/tickets/event/${idEvent}`;
    return this.http.get<any>(url).pipe(
      map((data) => {
        return data
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  getTicketsBySpace(idSpace: any) {
    const url = `${AppSettings.API_URI_V2}/tickets/space/${idSpace}`;
    return this.http.get<any>(url).pipe(
      map((data) => {
        return data
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  editPriceTicketLevel(ticketId: number, levelId: number, payload: any) {
    const url = `${AppSettings.API_URI_V2}/tickets/${ticketId}/levels/${levelId}`;
    return this.http.put<any>(url, payload).pipe(
      map((data) => {
        return data;
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  toggleTicket(ticketId: number, active: boolean) {
    const url = `${AppSettings.API_URI_V2}/toggle-active/tickets/${ticketId}`;
    return this.http.put<any>(url, { active }).pipe(
      map((data) => {
        return data;
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  toggleTicketLevel(ticketId: number, levelId: number, active: boolean) {
    const url = `${AppSettings.API_URI_V2}/toggle-active/tickets/${ticketId}/levels/${levelId}`;
    return this.http.put<any>(url, { active }).pipe(
      map((data) => {
        return data;
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  getTicketsBySession(idSession: number) {
    const url = `${AppSettings.API_URI_V2}/tickets/session/${idSession}`;
    return this.http.get<any>(url).pipe(
      map((data) => {
        return data
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  getTicketsBySessionSeat(idSession: number, idSeat: number) {
    const url = `${AppSettings.API_URI_V2}/tickets/session/${idSession}/seat/${idSeat}`;
    return this.http.get<any>(url).pipe(
      map((data) => {
        return data
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

}
